import { useState, useCallback } from 'react';
import { apiFetch } from '../../../utils/apiClient';

const useComments = (token, leadId, timestamp, fetchTaskDetail) => {
    const [addingComment, setAddingComment] = useState(false);
    const [editingComment, setEditingComment] = useState(null); // commentId being edited
    const [deletingComment, setDeletingComment] = useState(null); // commentId being deleted

    const handleAddComment = useCallback(async (taskId, commentText, isClosureComment = false) => {
        setAddingComment(true);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}/comment`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        comment: commentText,
                        leadId: leadId,
                        timestamp: timestamp,
                        isClosureComment: isClosureComment
                    })
                }
            );
            if (response.ok) {
                // Refresh task detail - don't throw if this fails since the comment was added
                try {
                    await fetchTaskDetail(taskId, false);
                } catch (refreshErr) {
                    console.error('Task refresh failed (comment added):', refreshErr);
                }
            } else {
                throw new Error('Failed to add comment');
            }
        } catch (err) {
            console.error('Comment error:', err);
            throw err;
        } finally {
            setAddingComment(false);
        }
    }, [leadId, timestamp, fetchTaskDetail]);

    const handleEditComment = useCallback(async (taskId, commentId, commentText) => {
        if (!commentText.trim()) {
            alert('Comment cannot be empty');
            return;
        }
        setEditingComment(commentId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}/comment/${encodeURIComponent(commentId)}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        comment: commentText,
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );
            if (response.ok) {
                await fetchTaskDetail(taskId, false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                alert(errorData.error || 'Failed to edit comment');
            }
        } catch (err) {
            console.error('Edit comment error:', err);
            alert('Failed to edit comment. Please try again.');
        } finally {
            setEditingComment(null);
        }
    }, [leadId, timestamp, fetchTaskDetail]);

    const handleDeleteComment = useCallback(async (taskId, commentId) => {
        if (!window.confirm('Are you sure you want to delete this comment?')) {
            return;
        }

        setDeletingComment(commentId);
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/task/${encodeURIComponent(taskId)}/comment/${encodeURIComponent(commentId)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        leadId: leadId,
                        timestamp: timestamp
                    })
                }
            );

            if (response.ok) {
                await fetchTaskDetail(taskId, false);
            } else {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.error || 'Failed to delete comment';
                alert(errorMessage);
                // Refresh to show current state
                await fetchTaskDetail(taskId, false);
            }
        } catch (err) {
            console.error('Delete comment error:', err);
            alert('Failed to delete comment. Please try again.');
        } finally {
            setDeletingComment(null);
        }
    }, [leadId, timestamp, fetchTaskDetail]);

    return {
        addingComment,
        editingComment,
        deletingComment,
        handleAddComment,
        handleEditComment,
        handleDeleteComment
    };
};

export default useComments;
